import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { getEffectiveCompanyId } from '../utils/userCompany';
import LoadingSpinner from '../components/LoadingSpinner';
import './AcceptInvitation.css';

const AcceptInvitation = () => {
  const { t } = useTranslation(['public', 'common']);
  const { user, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightToken = searchParams.get('invitation');

  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await axios.get('/api/companies/invitations');
      setInvitations(response.data.data || []);
    } catch (err) {
      setError(formatError(err) || t('common:failedAcceptInvitation'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (companyId) => {
    setActionId(companyId);
    setError('');
    try {
      await axios.post(`/api/companies/${companyId}/join`);
      await fetchUser();
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err) || t('common:failedAcceptInvitation'));
      setActionId(null);
    }
  };

  const handleDecline = async (companyId) => {
    setActionId(companyId);
    setError('');
    try {
      await axios.post(`/api/companies/${companyId}/decline`);
      setInvitations((prev) => prev.filter((inv) => inv.id !== companyId));
      setActionId(null);
    } catch (err) {
      setError(formatError(err) || t('common:failedDeclineInvitation'));
      setActionId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner className="accept-invitation-loading" />;
  }

  const activeCompanyId = getEffectiveCompanyId(user);

  return (
    <div className="accept-invitation-page">
      <div className="accept-invitation-container">
        <h1 className="accept-invitation-title">{t('public:invitationsTitle')}</h1>
        <p className="accept-invitation-subtitle">
          {activeCompanyId
            ? t('public:invitationsSubtitleExisting')
            : t('public:invitationsSubtitleNew')}
        </p>

        {error && <div className="accept-invitation-error">{error}</div>}

        {invitations.length === 0 ? (
          <div className="accept-invitation-empty">
            <p>{t('public:noInvitations')}</p>
            <button
              type="button"
              className="sim-button-primary"
              onClick={() => navigate(activeCompanyId ? '/dashboard' : '/no-company')}
            >
              {activeCompanyId ? t('public:backToDashboard') : t('public:goToSetup')}
            </button>
          </div>
        ) : (
          <div className="accept-invitation-list">
            {invitations.map((invitation) => {
              const isHighlighted =
                highlightToken &&
                (invitation.invitation_token === highlightToken ||
                  invitation.token === highlightToken);
              return (
                <div
                  key={invitation.id}
                  className={`accept-invitation-card${isHighlighted ? ' accept-invitation-card-highlighted' : ''}`}
                >
                  <div className="accept-invitation-card-content">
                    <h3>{invitation.name}</h3>
                    {invitation.invited_by_name && (
                      <p>{t('public:invitedBy', { name: invitation.invited_by_name })}</p>
                    )}
                  </div>
                  <div className="accept-invitation-actions">
                    <button
                      type="button"
                      className="sim-button-primary"
                      onClick={() => handleAccept(invitation.id)}
                      disabled={actionId === invitation.id}
                    >
                      {actionId === invitation.id ? t('public:accepting') : t('public:accept')}
                    </button>
                    <button
                      type="button"
                      className="sim-button-secondary"
                      onClick={() => handleDecline(invitation.id)}
                      disabled={actionId === invitation.id}
                    >
                      {t('public:decline')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitation;
